/**
 * `W5-T15` §4.2 — Stripe.js, loaded when a checkout mounts and not before (AC 4), once per
 * publishable key and locale (AC 5).
 *
 * `@stripe/stripe-js/pure` adds no script on import; `loadStripe` adds it. The memo is the
 * application's `QueryClient`, not a module-scope map: ADR-011 R5 forbids a module cache in
 * `src/features`, and the `QueryClient` is already the one cache per application instance. It is a
 * query in a component rather than a loader's because it must never run on a server, and because the
 * checkout is mounted by an action's answer (Pay now here, the award in `W4-T17`), not by a
 * navigation. A failure is not cached: Retry, or the next mount, asks again (AC 6).
 */
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe, StripeElementLocale } from '@stripe/stripe-js';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

/** Stripe.js and the Payment Element in the route's language; the storefront has two. */
export function stripeLocale(locale: string): StripeElementLocale {
  return locale === 'en' ? 'en' : 'es';
}

export function useStripeJs(publishableKey: string, locale: string): UseQueryResult<Stripe> {
  return useQuery({
    queryKey: ['stripe.js', publishableKey, locale] as const,
    queryFn: async () => {
      const stripe = await loadStripe(publishableKey, { locale: stripeLocale(locale) });
      // `null` is Stripe.js's answer where there is no `window`; to the client it is a form that did
      // not load.
      if (stripe === null) throw new Error('Stripe.js did not load');
      return stripe;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    // A `Stripe` instance is not data: it is kept as it is, never compared or copied.
    structuralSharing: false,
  });
}
